// src/pages/CodeGenerator.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, LinearProgress, Box
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";

/* --- Modal just handles form state --- */
function CodeGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  // NEW: keep the text in state so it can be edited
  const [textToVisualize, setTextToVisualize] = useState(initialText?.length > 0 ? initialText : "Describe code to be generated");
  const [vizType, setVizType] = useState("coding");
  const [vizSubtype, setVizSubtype] = useState("tryit");
  const [vizName, setVizName] = useState("code");
  const [loading, setLoading] = useState(false);

  // If initialText ever changes from the parent, sync it
  useEffect(() => {
    setTextToVisualize(initialText?.length > 0 ? initialText : "Describe coding to be generated");
  }, [initialText]);

  const handleSubmit = async () => {
    setLoading(true);
    // now pass the edited text back up, too
    try {
      await onGenerate(textToVisualize, { vizName, vizType, vizSubtype });
    } catch (e) {
      console.error(e)
      //setError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Coding Example and Exercise Generator</DialogTitle>
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* scrollable text area */}
        <Box sx={{ flex: '1 1 auto', maxHeight: '40vh', overflowY: 'auto' }}>
          <TextField
            fullWidth
            multiline
            // no longer disabled
            value={textToVisualize}
            onChange={e => setTextToVisualize(e.target.value)}
            margin="normal"
          />
        </Box>

        {/* sticky controls */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            pt: 2,
            pb: 1,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            label="Visual Name"
            fullWidth
            value={vizName}
            onChange={e => setVizName(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            label="Coding Type"
            fullWidth
            value={vizSubtype}
            onChange={e => setVizSubtype(e.target.value)}
            margin="normal"
          >
            <MenuItem value="tryit">Try it Yourself</MenuItem>
            <MenuItem value="exercise">Code Challenge</MenuItem>
            <MenuItem value="placeholder">Placeholder</MenuItem>
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* --- Page component --- */
export default function CodeGenerator() {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (text, courseId, lessonId, { vizName, vizType, vizSubtype  }) => {
      const body = {
        concept: text,
        course_id: courseId,
        lesson_id: lessonId,
        visual_type: vizType,
        visual_subtype: vizSubtype,
        visual_name: vizName
      };
      const { data } = await axios.post("/generate_codings/", body);
      return data;
    },
    [axios]
  );

  return (
    <ContentBuilderPage
      instructionText="Highlight text to create an interactive visual."
      modal={<CodeGeneratorModal />}
      generatorFn={generateVisualization}
      assetType="codings"
    />
  );
}
